import React, { useEffect } from 'react';
import LogRocket from 'logrocket';
import { Provider, useSelector } from 'react-redux';
import { userSelector } from '@store/user';
import { BrowserRouter as Router } from 'react-router-dom';
import './theme/style.scss';
import './theme/plugins.scss';
import './styles/darkMode.css';
import './styles/global.css';
import './styles/animations.css';
import ModalContainer from 'react-modal-promise';
import { ThemeProvider } from 'react-bootstrap';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import PersistentVideoPlayer from '@components/PersistentVideoPlayer';
import { RootState } from '@base/store';
import NewsletterPopupWrapper from '@components/NewsletterPopupWrapper';
import { UpdateNotificationModal } from '@components/UpdateNotificationModal';
import { useVersionChecker } from './hooks/useVersionChecker';

import Routes from './routes';
import { store } from './store';
import { PageLayout } from './components/Layout';
import { ToastContainer } from './components/Toaster';
import { ConfirmationContainer } from './components/Confirmation';

declare global {
    interface Window {
        dataLayer: any[];
    }
}

const AppContent: React.FC = () => {
    const isDarkMode = useSelector((state: RootState) => state.theme.isDarkMode);
    const user = useSelector(userSelector);

    // Version checker - checks for updates every 5 minutes
    const { updateAvailable, dismissUpdate, refreshApp } = useVersionChecker({
        checkInterval: 5 * 60 * 1000, // Check every 5 minutes
        enabled: process.env.NODE_ENV === 'production', // Only enable in production
        onUpdateDetected: () => {
            console.log('🚀 New version available! Showing update notification.');
        },
    });

    useEffect(() => {
        if (isDarkMode) {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }
    }, [isDarkMode]);

    useEffect(() => {
        // Identify user in LogRocket if user is logged in and LogRocket is not disabled
        if (user && user.id && process.env.REACT_APP_DISABLE_LOGROCKET !== 'true') {
            LogRocket.identify(user.id, {
                name: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : 'Name not available',
                email: user.email,
            });
        }
    }, [user]);

    return (
        <GoogleOAuthProvider clientId="247381769064-hm3kuiepovjukd0mjfohjfnns0ubvl3f.apps.googleusercontent.com">
            <ThemeProvider>
                <Router>
                    <DndProvider backend={HTML5Backend}>
                        <PageLayout user={store.getState().user}>
                            <ToastContainer />
                            <ConfirmationContainer />
                            <ModalContainer />
                            <NewsletterPopupWrapper>
                                <Routes />
                            </NewsletterPopupWrapper>
                        </PageLayout>
                    </DndProvider>
                </Router>
            </ThemeProvider>
            <PersistentVideoPlayer />
            
            {/* Update notification modal */}
            <UpdateNotificationModal
                show={updateAvailable}
                onRefresh={refreshApp}
                onDismiss={dismissUpdate}
            />
        </GoogleOAuthProvider>
    );
};

const App: React.FC = () => {
    return (
        <Provider store={store}>
            <AppContent />
        </Provider>
    );
};

export default App;
